import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plug, Check, X, Loader2, LogIn, RefreshCw, Unplug, Circle, AlertCircle, ShieldAlert, Flame, TrendingUp, TrendingDown, Droplets, BarChart2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXCHANGE_DATA = {
  pump_fun:    { name: 'Pump.fun',     chain: 'solana', wallet: 'phantom',  gradient: 'from-[#00D18C] to-[#00A86B]', letter: 'P' },
  raydium:     { name: 'Raydium',      chain: 'solana', wallet: 'phantom',  gradient: 'from-[#2B6DEF] to-[#1A4FBF]', letter: 'R' },
  jupiter:     { name: 'Jupiter',      chain: 'solana', wallet: 'phantom',  gradient: 'from-[#19FB9B] to-[#0EC880]', letter: 'J' },
  uniswap:     { name: 'Uniswap',      chain: 'evm',    wallet: 'metamask', gradient: 'from-[#FF007A] to-[#CC0062]', letter: 'U', disabled: true },
  pancakeswap: { name: 'PancakeSwap',  chain: 'bsc',    wallet: 'metamask', gradient: 'from-[#D1884F] to-[#A66A3F]', letter: 'C', disabled: true },
};

// Map chain names to exchange IDs for auto-fill
const CHAIN_TO_EXCHANGE = {
  solana:   'raydium',
  ethereum: 'uniswap',
  evm:      'uniswap',
  bsc:      'pancakeswap',
  base:     'uniswap',
  arbitrum: 'uniswap',
};

export default function ExchangeLogin() {
  const outletContext  = useOutletContext();
  const panicMode      = outletContext?.panicMode;
  const { isSubUser, isAdmin } = useAuth();

  // ── Exchange connection state ──────────────────────────────────
  const [connections,       setConnections]       = useState([]);
  const [adminConnections,  setAdminConnections]  = useState([]);
  const [showLoginModal,    setShowLoginModal]    = useState(false);
  const [selectedExchange,  setSelectedExchange]  = useState(null);
  const [loginSteps,        setLoginSteps]        = useState([]);
  const [loginInProgress,   setLoginInProgress]   = useState(false);
  const [loginComplete,     setLoginComplete]     = useState(false);
  const [walletAddress,     setWalletAddress]     = useState('');

  // ── Suggested Tokens state ─────────────────────────────────────
  const [suggestions,        setSuggestions]        = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsError,   setSuggestionsError]   = useState(null);

  // ── Fetch exchange connections ─────────────────────────────────
  const fetchConnections = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/exchange/connections`);
      setConnections(data.connections || []);
    } catch { /* silent */ }
  }, []);

  const fetchAdminConnections = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/exchange-status`);
      setAdminConnections(data.connected_exchanges || []);
    } catch {
      setAdminConnections(Object.keys(EXCHANGE_DATA));
    }
  }, []);

  // ── Fetch live suggested tokens from DexScreener (via backend) ──
  const fetchSuggestions = useCallback(async (forceRefresh = false) => {
    setLoadingSuggestions(true);
    setSuggestionsError(null);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/suggested-tokens${forceRefresh ? '?refresh=true' : ''}`
      );
      setSuggestions(data.tokens || data.suggestions || []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to fetch suggestions';
      setSuggestionsError(msg);
      if (err.response?.status !== 403 && err.response?.status !== 404) {
        toast.error(msg);
      }
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
    if (isSubUser) fetchAdminConnections();
    fetchSuggestions();                    // load on mount (uses cache if fresh)
  }, [fetchConnections, fetchAdminConnections, fetchSuggestions, isSubUser]);

  const isAdminConnected = (exchangeId) => {
    if (!isSubUser) return true;
    return adminConnections.includes(exchangeId);
  };

  // ── Auto-fill: clicking a suggestion copies address to clipboard
  //    and toasts with a prompt to paste into the bot token field ──
  const handleAutoFill = useCallback((token) => {
    if (token.address) {
      navigator.clipboard?.writeText(token.address).catch(() => {});
    }
    toast.success(
      `📋 ${token.symbol} address copied! Paste it into the token field on the Lure/Volume Bot page.`,
      { duration: 5000 }
    );
  }, []);

  // ── Wallet connection helpers ──────────────────────────────────
  const connectWallet = async (exchangeId) => {
    const ex = EXCHANGE_DATA[exchangeId];

    // EVM exchanges are disabled — this should never be called for them
    if (ex.disabled) return null;

    if (ex.wallet === 'phantom') {
      if (!window.solana?.isPhantom) {
        toast.error('Phantom wallet not found. Please install the Phantom Chrome extension.');
        return null;
      }
      try {
        // Disconnect first — clears Phantom trusted session, forces approval popup
        await window.solana.disconnect();
      } catch { /* ignore disconnect errors */ }
      try {
        const resp = await window.solana.connect({ onlyIfTrusted: false });
        return resp.publicKey.toString();
      } catch (err) {
        if (err.code === 4001) {
          toast.error('Connection rejected. Please approve the request in Phantom.');
        } else {
          toast.error('Failed to connect Phantom wallet. Please try again.');
        }
        return null;
      }
    }

    // EVM path disabled — never reached
    return null;
  };

  const handleStartLogin = async (exchangeId) => {
    setSelectedExchange(exchangeId);
    setLoginSteps([]);
    setLoginComplete(false);
    setShowLoginModal(true);

    const address = await connectWallet(exchangeId);
    if (!address) { setShowLoginModal(false); return; }

    setWalletAddress(address);
    setLoginInProgress(true);

    const ex         = EXCHANGE_DATA[exchangeId];
    const walletName = ex.wallet === 'phantom' ? 'Phantom' : 'MetaMask';
    const steps      = [{ step: 1, action: `${walletName} wallet connected`, status: 'loading' }];
    setLoginSteps([...steps]);

    const advance = async (msg, delay = 700) => {
      await new Promise(r => setTimeout(r, delay));
      steps[steps.length - 1].status = 'success';
      steps.push({ step: steps.length + 1, action: msg, status: 'loading' });
      setLoginSteps([...steps]);
    };

    await advance('Signing authentication message...', 600);
    await advance(`Connecting to ${ex.name} API...`, 800);
    await advance(`Authenticating ${address.slice(0, 8)}...${address.slice(-6)}`, 1000);
    await advance('Loading trading pairs...', 500);
    await new Promise(r => setTimeout(r, 700));
    steps[steps.length - 1].status = 'success';
    setLoginSteps([...steps]);

    try {
      await axios.post(`${API_URL}/api/exchange/login`, {
        exchange_id:    exchangeId,
        wallet_address: address,
        wallet_type:    ex.wallet,
      });
      try {
        await axios.post(`${API_URL}/api/wallet/connect`, {
          chain:       ex.chain,
          address,
          wallet_type: ex.wallet,
        });
      } catch { /* already connected */ }

      steps.push({ step: steps.length + 1, action: 'Session established — Ready to trade', status: 'success' });
      setLoginSteps([...steps]);
      setLoginComplete(true);
      toast.success(`Connected to ${ex.name}`);
      fetchConnections();
    } catch (err) {
      steps.push({ step: steps.length + 1, action: err.response?.data?.detail || 'Connection failed', status: 'error' });
      setLoginSteps([...steps]);
      toast.error('Exchange login failed');
    }
    setLoginInProgress(false);
  };

  const handleDisconnect = async (exchangeId) => {
    try {
      await axios.delete(`${API_URL}/api/exchange/disconnect/${exchangeId}`);
      toast.success('Disconnected');
      fetchConnections();
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const isConnected  = (id) => connections.some(c  => c.exchange_id === id);
  const getConnection = (id) => connections.find(c => c.exchange_id === id);

  // ── Helper: format large numbers ──────────────────────────────
  const fmtNum = (n) => {
    if (!n && n !== 0) return '—';
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
    return `$${n.toFixed(0)}`;
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="exchange-login-page">

      {/* Page heading */}
      <div>
        <h1 className="text-2xl font-bold font-['Outfit'] text-white flex items-center gap-2">
          <Plug className="w-7 h-7 text-[#E8520F]" /> Exchange Connections
        </h1>
        <p className="text-[hsl(215,16%,56%)]">
          Connect your wallet to DeFi exchanges to enable bot trading
        </p>
      </div>

      {/* Sub-user info banner */}
      {isSubUser && (
        <div className="p-3 rounded-lg border border-[#06b6d4]/20 bg-[#06b6d4]/5 flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 text-[#06b6d4] mt-0.5 shrink-0" />
          <p className="text-xs text-[#06b6d4]">
            Exchange availability is controlled by your administrator. Exchanges marked as unavailable
            have not been activated for your account — contact your admin to enable them.
          </p>
        </div>
      )}

      {/* No connections warning */}
      {connections.length === 0 && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-300">No Exchange Connections</p>
            <p className="text-xs text-amber-400/80 mt-0.5">
              Connect to at least one DeFi exchange before starting bots.
            </p>
          </div>
        </div>
      )}

      {/* ── Exchange Cards Grid ───────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(EXCHANGE_DATA).map(([id, ex]) => {
          const connected    = isConnected(id);
          const conn         = getConnection(id);
          const adminEnabled = isAdminConnected(id);

          return (
            <Card
              key={id}
              className={`border transition-all ${
                ex.disabled
                  ? 'border-[hsl(220,16%,18%)] opacity-40 cursor-not-allowed'
                  : !adminEnabled
                  ? 'border-[hsl(220,16%,18%)] opacity-60'
                  : connected
                  ? 'border-[#10B981]/30 bg-[#10B981]/[0.02]'
                  : 'border-[hsl(220,16%,18%)] hover:border-[#CBD5E1]'
              }`}
              data-testid={`exchange-login-${id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${ex.gradient} flex items-center justify-center`}>
                    <span className="text-white font-bold text-lg">{ex.letter}</span>
                  </div>
                  {!adminEnabled ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(220,16%,18%)] text-[hsl(215,16%,40%)] text-xs">
                      Unavailable
                    </span>
                  ) : connected ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-xs font-medium">
                      <Circle className="w-2 h-2 fill-green-500" /> Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(220,16%,18%)] text-gray-500 text-xs">
                      Disconnected
                    </span>
                  )}
                </div>

                <h3 className="text-lg font-semibold text-white">{ex.name}</h3>
                <p className="text-xs text-[hsl(215,16%,56%)] mt-0.5">
                  {ex.chain === 'solana' ? 'Solana' : ex.chain === 'bsc' ? 'BNB Chain' : 'Ethereum'} — Requires{' '}
                  {ex.wallet === 'phantom' ? 'Phantom' : 'MetaMask'}
                </p>

                {connected && conn && (
                  <div className="mt-3 p-2 bg-[hsl(220,16%,14%)] rounded border border-[hsl(220,16%,18%)]">
                    <p className="text-[10px] text-[hsl(215,16%,45%)]">Wallet</p>
                    <p className="text-xs font-mono text-white truncate">{conn.wallet_address}</p>
                    <p className="text-[10px] text-[hsl(215,16%,45%)] mt-1">
                      Since {new Date(conn.connected_at).toLocaleDateString()}
                    </p>
                  </div>
                )}

                <div className="mt-4">
                  {ex.disabled ? (
                    <div className="p-2 rounded bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)] text-center">
                      <p className="text-xs text-[hsl(215,16%,45%)]">
                        EVM — not supported for Solana bot trading
                      </p>
                    </div>
                  ) : !adminEnabled ? (
                    <div className="p-2 rounded bg-[hsl(220,16%,14%)] border border-[hsl(220,16%,18%)] text-center">
                      <p className="text-xs text-[hsl(215,16%,45%)]">Not available — contact your administrator</p>
                    </div>
                  ) : connected ? (
                    <div className="flex gap-2">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleStartLogin(id)}
                        className="border-[#E8520F]/20 text-[#E8520F] hover:bg-[#E8520F]/5 text-xs flex-1"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" /> Reconnect
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleDisconnect(id)}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                      >
                        <Unplug className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleStartLogin(id)}
                      disabled={panicMode}
                      className="bg-[#E8520F] hover:bg-[#C9440C] text-white text-xs w-full"
                    >
                      <LogIn className="w-3 h-3 mr-1" /> Connect &amp; Login
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          🔥 SUGGESTED TOKENS — Live DexScreener feed
          Placed below exchange cards per commander.gaurilabs.com spec
          Click any token → copies contract address to clipboard
          ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <div
        className="rounded-xl border border-[#E8520F]/25 bg-[#E8520F]/[0.04]"
        data-testid="suggested-tokens-panel"
      >
        {/* Header row */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[hsl(220,16%,18%)]">
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-[#E8520F]" />
            <span className="text-sm font-semibold text-[#E8520F]">
              Suggested Tokens <span className="text-[hsl(215,16%,45%)] font-normal">(≥$100K liquidity)</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[hsl(215,16%,40%)] font-mono hidden sm:block">
              DexScreener · Click token to copy address
            </span>
            <button
              onClick={() => fetchSuggestions(true)}
              disabled={loadingSuggestions}
              title="Refresh live suggestions from DexScreener"
              className="p-1.5 rounded-lg hover:bg-[hsl(220,16%,18%)] text-[hsl(215,16%,50%)]
                         hover:text-[#E8520F] transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loadingSuggestions ? 'animate-spin text-[#E8520F]' : ''}`} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-3">

          {/* Loading skeletons */}
          {loadingSuggestions && (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div
                  key={i}
                  className="h-14 rounded-lg bg-[hsl(220,16%,15%)] animate-pulse"
                  style={{ animationDelay: `${i * 80}ms` }}
                />
              ))}
            </div>
          )}

          {/* Error state */}
          {!loadingSuggestions && suggestionsError && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
              <div>
                <p className="text-sm text-red-400">{suggestionsError}</p>
                <button
                  onClick={() => fetchSuggestions(true)}
                  className="text-xs text-[#E8520F] hover:underline mt-0.5"
                >
                  Try again →
                </button>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!loadingSuggestions && !suggestionsError && suggestions.length === 0 && (
            <div className="text-center py-8">
              <Flame className="w-10 h-10 mx-auto mb-3 text-[hsl(215,16%,30%)]" />
              <p className="text-sm text-[hsl(215,16%,45%)]">No suggestions available</p>
              <p className="text-xs text-[hsl(215,16%,35%)] mt-1">
                Admin only · DexScreener · Click 🔄 to fetch live tokens
              </p>
              <button
                onClick={() => fetchSuggestions(true)}
                className="mt-3 text-xs text-[#E8520F] hover:underline"
              >
                Fetch now →
              </button>
            </div>
          )}

          {/* Token list */}
          {!loadingSuggestions && !suggestionsError && suggestions.length > 0 && (
            <div className="space-y-2">
              {suggestions.map((token, i) => {
                const positive = (token.price_change_24h ?? 0) >= 0;
                return (
                  <div
                    key={token.address || i}
                    onClick={() => handleAutoFill(token)}
                    title="Click to copy contract address"
                    className="flex items-center justify-between gap-3 p-3 rounded-lg
                               bg-[hsl(220,16%,13%)] border border-[hsl(220,16%,19%)]
                               hover:border-[#E8520F]/40 hover:bg-[#E8520F]/[0.04]
                               cursor-pointer transition-all group"
                    data-testid={`suggestion-${i}`}
                  >
                    {/* Rank + token info */}
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs font-mono text-[hsl(215,16%,38%)] w-4 shrink-0 text-center">
                        {i + 1}
                      </span>

                      {/* Name + badges */}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-bold text-white truncate max-w-[80px]">
                            {token.symbol}
                          </span>
                          {/* Chain badge */}
                          <span className="px-1.5 py-0.5 rounded bg-[hsl(220,16%,20%)]
                                           text-[hsl(215,16%,55%)] text-[10px] font-mono uppercase shrink-0">
                            {token.chain}
                          </span>
                          {/* Age badge */}
                          {token.pair_age_hours != null && (
                            <span className="flex items-center gap-0.5 text-[10px] text-[hsl(215,16%,40%)] shrink-0">
                              <Clock className="w-2.5 h-2.5" />
                              {token.pair_age_hours}h
                            </span>
                          )}
                        </div>

                        {/* Stats row */}
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          <span className="flex items-center gap-1 text-[10px] text-[hsl(215,16%,44%)] font-mono">
                            <Droplets className="w-2.5 h-2.5 text-[#06b6d4]" />
                            {fmtNum(token.liquidity_usd)}
                          </span>
                          <span className="flex items-center gap-1 text-[10px] text-[hsl(215,16%,44%)] font-mono">
                            <BarChart2 className="w-2.5 h-2.5 text-[#8b5cf6]" />
                            {fmtNum(token.volume_24h_usd)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right: price change + copy hint */}
                    <div className="text-right shrink-0">
                      <div className={`flex items-center gap-0.5 justify-end text-sm font-bold font-mono
                        ${positive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                        {positive
                          ? <TrendingUp className="w-3.5 h-3.5" />
                          : <TrendingDown className="w-3.5 h-3.5" />
                        }
                        {positive ? '+' : ''}{(token.price_change_24h ?? 0).toFixed(1)}%
                      </div>
                      <p className="text-[10px] text-[hsl(215,16%,38%)] group-hover:text-[#E8520F]
                                    transition-colors mt-0.5">
                        copy address →
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>

        {/* Footer */}
        {!loadingSuggestions && suggestions.length > 0 && (
          <div className="px-4 py-2.5 border-t border-[hsl(220,16%,18%)] flex items-center justify-between">
            <span className="text-[10px] text-[hsl(215,16%,38%)] font-mono">
              {suggestions.length} token{suggestions.length !== 1 ? 's' : ''} · sorted by 24h gain
            </span>
            <button
              onClick={() => fetchSuggestions(true)}
              disabled={loadingSuggestions}
              className="text-[10px] text-[#E8520F] hover:underline disabled:opacity-50"
            >
              🔄 Refresh live data
            </button>
          </div>
        )}
      </div>

      {/* ── Login Modal ──────────────────────────────────────────── */}
      <Dialog
        open={showLoginModal}
        onOpenChange={(open) => { if (!loginInProgress) setShowLoginModal(open); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white font-['Outfit']">
              <LogIn className="w-5 h-5 text-[#E8520F]" />
              {selectedExchange && EXCHANGE_DATA[selectedExchange]
                ? `Connecting to ${EXCHANGE_DATA[selectedExchange].name}`
                : 'Exchange Login'}
            </DialogTitle>
            <DialogDescription className="text-[hsl(215,16%,56%)]">
              Authenticating via wallet signature
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 mt-3">
            {loginSteps.map((step, i) => (
              <div
                key={i}
                className={`flex items-center gap-3 p-2.5 rounded-lg ${
                  step.status === 'success' ? 'bg-green-500/5'   :
                  step.status === 'loading' ? 'bg-[#E8520F]/5'   :
                  step.status === 'error'   ? 'bg-red-500/10'    :
                                              'bg-[hsl(220,16%,14%)]'
                }`}
              >
                {step.status === 'success' && <Check   className="w-4 h-4 text-green-400 shrink-0" />}
                {step.status === 'loading' && <Loader2 className="w-4 h-4 text-[#E8520F] animate-spin shrink-0" />}
                {step.status === 'error'   && <X       className="w-4 h-4 text-red-400 shrink-0" />}
                <p className={`text-sm ${
                  step.status === 'success' ? 'text-green-400'         :
                  step.status === 'loading' ? 'text-[#E8520F]'         :
                  step.status === 'error'   ? 'text-red-400'           :
                                              'text-[hsl(215,16%,56%)]'
                }`}>
                  {step.action}
                </p>
              </div>
            ))}
          </div>

          {loginComplete && (
            <>
              <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-center">
                <Check className="w-6 h-6 text-green-400 mx-auto mb-1" />
                <p className="text-sm font-medium text-green-400">
                  Connected to {EXCHANGE_DATA[selectedExchange]?.name}
                </p>
              </div>
              <Button
                onClick={() => setShowLoginModal(false)}
                variant="outline"
                className="w-full mt-2 border-[hsl(220,16%,18%)]"
              >
                Done
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
